import { useEffect, useState } from 'react'

/**
 * usePersistedState — drop-in replacement for `useState` that persists
 * the value to localStorage under the given key. Use this for transient
 * form state (drafts of order forms, half-filled fields) so the doctor
 * doesn't lose their work to:
 *   - HMR re-mounts during local development
 *   - browser back / accidental tab close
 *   - any parent component re-key that would normally re-mount the form
 *
 * Contract:
 *   - Reads localStorage[key] on mount; falls back to `defaultValue`
 *   - Writes to localStorage on every state change
 *   - Removes the key entirely when value is "empty" (empty string,
 *     empty array, null, undefined) — keeps storage tidy
 *   - Storage errors (quota, private mode) are swallowed silently —
 *     state still works, it just doesn't persist
 *
 * Key convention: prefix with the form/feature name and include the
 * encounter / patient / parent-row id so two open encounters don't
 * clobber each other. E.g. `amb-pickup-${encounter.id}`.
 *
 * Usage:
 *   const [pickup, setPickup] = usePersistedState(
 *     `amb-pickup-${encounter.id}`,
 *     '',
 *   )
 *
 * To clear the persisted draft (e.g. after successful submit) just
 * reset the state to its empty form — the effect below removes the
 * key automatically.
 */
export default function usePersistedState(key, defaultValue) {
  const [value, setValue] = useState(() => {
    if (typeof window === 'undefined') return defaultValue
    try {
      const raw = window.localStorage.getItem(key)
      if (raw == null) return defaultValue
      return JSON.parse(raw)
    } catch {
      return defaultValue
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const isEmpty =
        value == null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      if (isEmpty) {
        window.localStorage.removeItem(key)
      } else {
        window.localStorage.setItem(key, JSON.stringify(value))
      }
    } catch {
      // localStorage may throw QuotaExceededError or be blocked in
      // private browsing — fail silently, the state still works.
    }
  }, [key, value])

  return [value, setValue]
}
